import { useRef, type ReactNode } from "react";

/**
 * Source of truth for the {@link ScheduleTab} union. `as const` makes the
 * tuple immutable AND lets us derive both the union type AND a runtime
 * predicate from a single declaration. Matches the
 * `ROOM_TYPES` / `REQUEST_PRIORITIES` / `REASSIGN_FIT_STATUSES` pattern in
 * `types.ts`. The four tabs are ordered for consumer convenience (e.g.
 * arrow-key navigation); render order in the tab bar is set by the
 * separate {@link TABS} array below.
 */
export const SCHEDULE_TABS = [
  "calendar",
  "issues",
  "sections",
  "runs",
] as const;

export type ScheduleTab = (typeof SCHEDULE_TABS)[number];

/**
 * Runtime type-guard. Returns `true` iff `raw` is one of the known tab ids.
 * Used by URL-param parsing so a typo in `?tab=` falls through to the
 * default ("calendar") instead of crashing or silently widening the union.
 */
// eslint-disable-next-line react-refresh/only-export-components -- co-located config + predicate keeps tab union single-source-of-truth
export function isScheduleTab(raw: string): raw is ScheduleTab {
  return (SCHEDULE_TABS as readonly string[]).includes(raw);
}

interface ScheduleTabsProps {
  active: ScheduleTab;
  onChange: (tab: ScheduleTab) => void;
  /**
   * Optional per-tab badge node rendered to the right of the label (e.g. an
   * issue-count pill on the Validation issues tab or a section count on the
   * Sections tab). Phase 4 Slice 6 introduces these; consumers that don't
   * need any badges can simply omit the prop.
   */
  badges?: Partial<Record<ScheduleTab, ReactNode>>;
}

/**
 * Tab order matches the Phase 4 / 5 prototype (`Calendar grid`, `Sections`,
 * `Validation issues`, `Runs`). Keep the ordering in sync with the prototype
 * rather than alphabetizing — the visual rhythm "see the schedule → see the
 * slots the scheduler produced → see what didn't fit → see how this run
 * compares to past runs" is intentional.
 */
const TABS: ReadonlyArray<{ id: ScheduleTab; label: string }> = [
  { id: "calendar", label: "Calendar grid" },
  { id: "sections", label: "Sections" },
  { id: "issues", label: "Validation issues" },
  { id: "runs", label: "Runs" },
];

export function ScheduleTabs({ active, onChange, badges }: ScheduleTabsProps) {
  // Roving-tabindex ref map. Index matches the position in `TABS`.
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    idx: number,
  ) => {
    const count = TABS.length;
    let nextIdx: number | null = null;

    if (e.key === "ArrowRight") nextIdx = (idx + 1) % count;
    else if (e.key === "ArrowLeft") nextIdx = (idx - 1 + count) % count;
    else if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = count - 1;

    if (nextIdx === null) return;
    e.preventDefault();
    const nextTab = TABS[nextIdx];
    if (!nextTab) return;
    onChange(nextTab.id);
    requestAnimationFrame(() => {
      tabRefs.current[nextIdx as number]?.focus();
    });
  };

  return (
    <div
      data-testid="schedule-tabs"
      role="tablist"
      aria-label="Schedule view"
      className="inline-flex bg-surface border border-border-subtle rounded-lg p-[3px] mb-4"
    >
      {TABS.map((tab, idx) => {
        const isActive = active === tab.id;
        const badge = badges?.[tab.id];
        return (
          <button
            key={tab.id}
            ref={(el) => {
              tabRefs.current[idx] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            data-testid={`schedule-tab-${tab.id}`}
            onClick={() => onChange(tab.id)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className={`px-3.5 py-1.5 rounded-md text-[13px] font-medium cursor-pointer flex items-center gap-2 ${
              isActive
                ? "bg-surface-2 text-white"
                : "text-text-muted hover:text-white"
            }`}
          >
            {tab.label}
            {badge !== undefined && badge !== null && (
              <span data-testid={`schedule-tab-${tab.id}-badge`}>{badge}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
